/**
 * useUser Composable
 * 
 * A composable that provides user-related functionality and state management.
 * It handles user data access, authentication state, and user initials generation.
 * 
 * Features:
 * - Current user data access
 * - Authentication state checking
 * - User initials generation from name
 * 
 * @returns {Object} An object containing user data, auth state, and user initials
 */

import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

export function useUser() {
  const authStore = useAuthStore()

  const userInitials = computed(() => {
    const user = authStore.currentUser
    if (!user?.user_details) return ''
    
    const { first_name, last_name } = user.user_details
    const initials = `${first_name?.[0] || ''}${last_name?.[0] || ''}`
    return initials.toUpperCase()
  })

  const userName = computed(() => {
    const user = authStore.currentUser
    return user?.user_details?.full_name || user?.email || ''
  })

  const userFirstName = computed(() => {
    return authStore.currentUser?.user_details?.first_name || ''
  })

  const userLastName = computed(() => {
    return authStore.currentUser?.user_details?.last_name || ''
  })

  const userMiddleName = computed(() => {
    return authStore.currentUser?.user_details?.middle_name || ''
  })

  const userCity = computed(() => {
    return authStore.currentUser?.user_details?.city || ''
  })

  const userState = computed(() => {
    return authStore.currentUser?.user_details?.state || ''
  })

  const userZipCode = computed(() => {
    return authStore.currentUser?.user_details?.zip_code || ''
  })

  const userPhoneNumber = computed(() => {
    return authStore.currentUser?.user_details?.phone_number || ''
  })

  const userEmail = computed(() => {
    return authStore.currentUser?.email || ''
  })

  const userImage = computed(() => {
    return authStore.currentUser?.user_details?.user_image || ''
  })

  const userType = computed(() => {
    return authStore.currentUser?.user_type_label || ''
  })

  const isActive = computed(() => {
    return authStore.currentUser?.user_status || false
  })

  const passwordChangedTimeAgo = computed(() => {
    return authStore.currentUser?.password_changed_time_ago || 'Never changed'
  })

  const currentUser = computed(() => authStore.currentUser)
  const isAuthenticated = computed(() => authStore.isAuthenticated)

  return {
    userInitials,
    userName,
    userEmail,
    userImage,
    userType,
    currentUser,
    isAuthenticated,
    isActive,
    userFirstName,
    userLastName,
    userMiddleName,
    userCity,
    userState,
    userZipCode,
    userPhoneNumber,
    passwordChangedTimeAgo
  }
} 